import type { ReactNode } from 'react';
import { toast } from 'react-hot-toast';
import { MdClose as CloseIcon } from 'react-icons/md';

import ButtonUnstyled from 'components/Common/ButtonUnstyled';

import L10n from 'helpers/L10n';

import { ReactComponent as WarningIcon } from 'images/warningFilledWhite.svg';

import Tokens from 'styles/tokens';

import {
  reportErrorToast,
  type IApiContext,
  type IReportErrorToastOptions
} from './reportErrorToast';

interface INotificationProps
  extends Omit<IReportErrorToastOptions, 'api' | 'message' | 'variant'> {
  message: ReactNode;
  duration?: number;
  api?: IApiContext;
}

interface IToastInstance {
  id: string;
}

const TOAST_POSITION = 'bottom-left';
const HOT_TOAST_MESSAGE_SIDE_MARGIN = '10px';

const renderToastContent = (
  message: ReactNode,
  toastInstance: IToastInstance
) => (
  <div style={styles.content}>
    <span style={styles.message}>{message}</span>
    <ButtonUnstyled
      onClick={() => toast.dismiss(toastInstance.id)}
      type='button'
      tooltip={L10n.general.close}
      style={styles.closeButton}
    >
      <CloseIcon style={styles.closeIcon} />
    </ButtonUnstyled>
  </div>
);

export const successNotification = ({
  message,
  duration = 10000
}: INotificationProps) => {
  const toastId = toast.success(
    toastInstance => renderToastContent(message, toastInstance),
    {
      position: TOAST_POSITION,
      duration,
      style: {
        ...styles.success,
        ...styles.toastStyles
      },
      iconTheme: {
        primary: Tokens.colors.white,
        secondary: Tokens.colors.greenDark
      }
    }
  );

  return () => toast.dismiss(toastId);
};

export const errorNotification = ({
  message,
  duration = 10000,
  ...reportingOptions
}: INotificationProps) => {
  reportErrorToast({
    ...reportingOptions,
    message,
    variant: 'error'
  });

  const toastId = toast.error(
    toastInstance => renderToastContent(message, toastInstance),
    {
      position: TOAST_POSITION,
      duration,
      style: {
        ...styles.error,
        ...styles.toastStyles
      },
      iconTheme: {
        primary: Tokens.colors.white,
        secondary: Tokens.colors.redDark
      }
    }
  );

  return () => toast.dismiss(toastId);
};

export const warningNotification = ({
  message,
  duration = 10000
}: INotificationProps) => {
  const toastId = toast.error(
    toastInstance => renderToastContent(message, toastInstance),
    {
      position: TOAST_POSITION,
      duration,
      style: {
        ...styles.warning,
        ...styles.toastStyles
      },
      icon: <WarningIcon className='h-12 w-12' />
    }
  );

  return () => toast.dismiss(toastId);
};

const styles = {
  toastStyles: {
    ...Tokens.type.bodyS,
    borderRadius: Tokens.spacing.borderRadius,
    padding: `${Tokens.spacing.one} ${Tokens.spacing.two}`,
    color: Tokens.colors.white,
    fontWeight: 'bold'
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: `calc(100% + ${HOT_TOAST_MESSAGE_SIDE_MARGIN})`,
    marginRight: `-${HOT_TOAST_MESSAGE_SIDE_MARGIN}`,
    boxSizing: 'border-box' as const
  },
  message: {
    flex: 1,
    minWidth: 0,
    paddingRight: Tokens.spacing.one,
    // Break long unbreakable tokens so the message wraps
    // within the toast instead of overflowing its container.
    overflowWrap: 'anywhere' as const
  },
  closeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
    flexShrink: 0
  },
  closeIcon: {
    color: Tokens.colors.white,
    fontSize: Tokens.spacing.three
  },
  success: {
    background: Tokens.colors.greenDark
  },
  error: {
    background: Tokens.colors.redDark
  },
  warning: {
    background: Tokens.colors.yellowMoreDark
  }
};
